import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { RuotaMark } from "./RuotaMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#4A5557]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "w-full border-b border-[#0D1F23]/25 bg-transparent py-3 text-base text-[#0D1F23] placeholder-[#4A5557]/50 outline-none transition-colors duration-300 focus:border-[#168A91]";

export const EnquiryForm = ({ preselected }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    destination: "",
    dates: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!preselected) return;
    setSubmitted(false);
    setForm((f) => ({
      ...f,
      destination: f.destination ? `${f.destination}, ${preselected}` : preselected,
    }));
  }, [preselected]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        dates: form.dates,
        budget: form.budget,
        message: form.message,
        destinations: form.destination.trim() ? [form.destination.trim()] : [],
      };
      await axios.post(`${API}/enquiries`, payload);
      confetti({
        particleCount: 130,
        spread: 75,
        origin: { y: 0.7 },
        colors: ["#168A91", "#E2725B", "#F7F0E3"],
      });
      toast.success("Your journey is in motion. We'll be in touch shortly.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Something interrupted the journey. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="bg-[#EBE2D0] py-24 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-accent text-xs font-bold uppercase tracking-[0.25em] text-[#E2725B]">
                Begin Here
              </p>
              <h2 className="mt-5 font-display text-3xl font-light leading-tight tracking-tight text-[#0D1F23] sm:text-5xl">
                Start the <span className="italic text-[#E2725B]">conversation</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A5557] sm:text-lg">
                Tell us where your curiosity is pointing, and a real human who designs
                extraordinary travel will reply within 24 hours.
              </p>
              <div className="mt-10 hidden lg:block">
                <RuotaMark size={150} duration={30} className="opacity-60" />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    data-testid="enquiry-success-message"
                    className="flex min-h-[420px] flex-col items-center justify-center rounded-3xl bg-[#0D1F23] p-10 text-center text-[#F7F0E3]"
                  >
                    <h3 className="font-display text-3xl font-light sm:text-4xl">
                      Thank you! Your plans are <span className="italic text-[#E2725B]">in motion</span>.
                    </h3>
                    <p className="mt-4 max-w-md text-[#F7F0E3]/70">
                      A confirmation is on its way to your inbox, and an advisor will be in touch
                      personally. The world is waiting.
                    </p>
                    <button
                      data-testid="enquiry-plan-another-button"
                      onClick={() => {
                        setSubmitted(false);
                        setForm({ name: "", email: "", destination: "", dates: "", budget: "", message: "" });
                      }}
                      className="mt-8 rounded-full border border-[#F7F0E3]/30 px-6 py-3 font-accent text-[11px] uppercase tracking-[0.2em] transition-colors hover:border-[#E2725B] hover:text-[#E2725B]"
                    >
                      Plan another journey
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    onSubmit={submit}
                    data-testid="enquiry-form"
                    className="rounded-3xl bg-[#F7F0E3] p-8 shadow-[0_20px_60px_-30px_rgba(13,31,35,0.35)] sm:p-10"
                  >
                    <div className="grid gap-7 sm:grid-cols-2">
                      <Field label="Full Name">
                        <input
                          data-testid="enquiry-input-name"
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="Ada Moretti"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          data-testid="enquiry-input-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                          placeholder="ada@example.com"
                          className={inputCls}
                        />
                      </Field>
                    </div>

                    <div className="mt-8" data-testid="enquiry-field-destination">
                      <Field label="Destination Interest">
                        <input
                          data-testid="enquiry-input-destination"
                          value={form.destination}
                          onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
                          placeholder="Tell us places you're dreaming of"
                          className={inputCls}
                        />
                      </Field>
                    </div>

                    <div className="mt-8 grid gap-7 sm:grid-cols-2">
                      <Field label="Travel Dates">
                        <input
                          data-testid="enquiry-input-dates"
                          value={form.dates}
                          onChange={(e) => setForm((f) => ({ ...f, dates: e.target.value }))}
                          placeholder="Late September, ~10 days"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Budget Range">
                        <input
                          data-testid="enquiry-input-budget"
                          value={form.budget}
                          onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                          placeholder="e.g. $10k - 20k, or flexible"
                          className={inputCls}
                        />
                      </Field>
                    </div>

                    <div className="mt-8">
                      <Field label="Anything else we should know?">
                        <textarea
                          data-testid="enquiry-input-message"
                          rows={3}
                          value={form.message}
                          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                          placeholder="Special occasion, bucket list, birthday"
                          className={`${inputCls} resize-none`}
                        />
                      </Field>
                    </div>

                    <button
                      data-testid="enquiry-submit-button"
                      type="submit"
                      disabled={submitting}
                      className="group mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-[#168A91] py-4 font-accent text-[11px] uppercase tracking-[0.25em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#116D73] disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={15} className="animate-spin" /> Setting wheels in motion…
                        </>
                      ) : (
                        <>
                          Send My Inquiry
                          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
